import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Loading from 'components/Loading';
import PermissionGates from 'components/PermissionGates';
import AccountPickerContext from 'contexts/AccountPickerContext';
import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import FormInput from 'kingpin/forms/FormInput';
import usePopup from 'hooks/usePopup';
import isDefined from 'isDefined';
import PERMISSIONS from 'permissionDefinitions';
import STORE from 'store';
import { V3AppsContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import useGrantRevokeRole from '../../../../../hooks/useGrantRevokeRole';
import USER_ROLES from '../../../../../roles';
import { HeaderDiv, IconDiv, ListDiv, ListRow } from './AppViewersList';
import useAdminsAndEditors from './useEditors';

// Adding/removing an admin on a BONUS app also grants/revokes the
// BONUS_ADMINISTRATOR role, which requires the acting user to hold a
// user-management permission (MODIFY_USER). Editors and viewers don't have it,
// so hide these actions for them on bonus apps rather than surface a button
// whose role grant would 403. Non-bonus apps have no role side-effect, so they
// are left ungated.
const BonusAdminActionGate = ({
  app,
  children,
}: {
  app: V3.App;
  children: JSX.Element;
}) => {
  if (!appTypeCheckers.isBonusApp(app)) {
    return <>{children}</>;
  }

  return (
    <PermissionGates.Has
      requiredPermission={PERMISSIONS.USER_MANAGEMENT.MODIFY_USER}
    >
      {children}
    </PermissionGates.Has>
  );
};

const UserRow = ({
  user,
  app,
}: {
  user: UserManagement.SignedUpUser | UserManagement.PendingUser;
  app: V3.App;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { allProductApps } = useContext(V3AppsContext);
  const { revokeRole } = useGrantRevokeRole();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // The BONUS_ADMINISTRATOR role is shared by every bonus app, so it must only
  // be revoked once the user no longer admins any of them
  const isAdminOfAnotherBonusApp = useMemo(
    () =>
      allProductApps.some(
        (a) =>
          a.id !== app.id &&
          appTypeCheckers.isBonusApp(a) &&
          a.adminUserIds.includes(user.id),
      ),
    [allProductApps, app.id, user.id],
  );
  const onRemoveClicked = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const newApp: V3.App = {
      ...app,
      adminUserIds: app.adminUserIds.filter((userId) => userId !== user.id),
    };

    const firestoreWrite = (() => {
      if (appTypeCheckers.isWorkspace(newApp)) {
        return STORE.contentDistributions
          .getWorkspacesRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isBonusApp(newApp)) {
        return STORE.contentDistributions
          .getBonusAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
        return STORE.contentDistributions
          .getLegacyBonusAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isGeneralApp(newApp)) {
        return STORE.contentDistributions
          .getGeneralAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      }
      return Promise.resolve();
    })();

    const roleRevoke =
      appTypeCheckers.isBonusApp(app) && !isAdminOfAnotherBonusApp
        ? revokeRole(user.id, USER_ROLES.BONUS_ADMINISTRATOR)
        : Promise.resolve();

    Promise.all([firestoreWrite, roleRevoke]).then(() => {
      setIsSaving(false);
    });
  }, [
    user.id,
    isSaving,
    revokeRole,
    selectedAccountId,
    app,
    isAdminOfAnotherBonusApp,
  ]);

  return (
    <ListRow>
      <Row>
        <IconDiv />
        <Typography.Body type={'Body 14'}>{user.displayName}</Typography.Body>
      </Row>
      <div className="showOnMouseOver">
        <BonusAdminActionGate app={app}>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Remove'}
            onClick={onRemoveClicked}
          />
        </BonusAdminActionGate>
      </div>
    </ListRow>
  );
};

const AddAdminsPopup = ({
  app,
  close,
  availableUsers,
}: {
  app: V3.App;
  close: () => void;
  availableUsers: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { grantRole } = useGrantRevokeRole();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const options = useMemo((): DropdownOption[] => {
    return [...availableUsers]
      .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''))
      .map((user) => {
        return {
          key: user.id,
          label: user.displayName,
          extraSearchContext: user.email,
          isSelected: selectedUserIds.some((uid) => uid === user.id),
          onSelected: () => {
            setSelectedUserIds((current) => {
              if (current.some((uid) => uid === user.id)) {
                return current.filter((uid) => uid !== user.id);
              }
              return [...current, user.id];
            });
          },
        };
      });
  }, [availableUsers, selectedUserIds]);

  const onAddClicked = useCallback(() => {
    if (isSaving || selectedUserIds.length === 0) {
      return;
    }

    setIsSaving(true);
    const newApp: V3.App = {
      ...app,
      adminUserIds: [...app.adminUserIds, ...selectedUserIds],
    };

    const firestoreWrite = (() => {
      if (appTypeCheckers.isWorkspace(newApp)) {
        return STORE.contentDistributions
          .getWorkspacesRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isBonusApp(newApp)) {
        return STORE.contentDistributions
          .getBonusAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
        return STORE.contentDistributions
          .getLegacyBonusAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isGeneralApp(newApp)) {
        return STORE.contentDistributions
          .getGeneralAppsRef({ accountId: selectedAccountId })
          .doc(newApp.id)
          .set(newApp);
      } else {
        const e = new Error();
        e.name = 'Unknown app type';
        throw e;
      }
    })();

    const roleGrants = appTypeCheckers.isBonusApp(app)
      ? selectedUserIds.map((userId) =>
          grantRole(userId, USER_ROLES.BONUS_ADMINISTRATOR),
        )
      : [];

    return Promise.all([firestoreWrite, ...roleGrants]).then(() => {
      setIsSaving(false);
      close();
    });
  }, [isSaving, selectedUserIds, app, grantRole, selectedAccountId, close]);

  const Content = useMemo(() => {
    if (options.length === 0) {
      return (
        <>
          <FormContent>
            <Typography.Body type={'Body 14'}>
              We couldn't find any Executive or System Admins who were eligible
              to be added as an Admin of this content
            </Typography.Body>
          </FormContent>
          <Row spaceBetween>
            <Button
              size={'Medium'}
              type={'Secondary'}
              label={'Cancel'}
              onClick={close}
            />
          </Row>
        </>
      );
    }

    return (
      <>
        <FormContent>
          <FormInput label={'Admins to add'}>
            <Inputs.Dropdown
              options={options}
              testId={'add-admins-dropdown'}
              isSearchEnabled
              searchPlaceholder={'Search'}
            />
          </FormInput>
        </FormContent>
        <Row spaceBetween>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Cancel'}
            onClick={close}
          />
          <Button
            size={'Medium'}
            type={'Primary'}
            label={'Add Admins'}
            isDisabled={selectedUserIds.length === 0}
            isLoading={isSaving}
            onClick={onAddClicked}
            testId={'confirm-add-admins'}
          />
        </Row>
      </>
    );
  }, [close, isSaving, onAddClicked, options, selectedUserIds.length]);

  return (
    <Form>
      <FormHeader title={'Add Admins'} onClose={close} />
      {Content}
    </Form>
  );
};

const AppAdminsList = ({ app }: { app: V3.App }) => {
  const { isOpen, open, close } = usePopup();
  const { adminsAndEditors, isLoading } = useAdminsAndEditors();

  const [searchText, setSearchText] = useState<string>('');
  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const users = useMemo(() => {
    return app.adminUserIds
      .map((uid) => adminsAndEditors.find((u) => u.id === uid))
      .filter(isDefined)
      .filter((u) => {
        if (searchText === '') {
          return true;
        }

        return (u.displayName ?? '')
          .toLowerCase()
          .includes(searchText.toLowerCase());
      });
  }, [app.adminUserIds, adminsAndEditors, searchText]);

  const adminsAndEditorsWhoAreNotAddedYet = useMemo(() => {
    return adminsAndEditors.filter(
      (user) => !app.adminUserIds.includes(user.id),
    );
  }, [adminsAndEditors, app.adminUserIds]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Row spaceBetween centerAlign style={{ height: 64 }}>
        <div style={{ width: 300 }}>
          <TextInput
            value={searchText}
            onChange={onSearchTextChanged}
            icon={'search'}
            placeholder={'Search'}
          />
        </div>

        <BonusAdminActionGate app={app}>
          <Button
            size={'Medium'}
            type={'Primary'}
            label={'Add Admins'}
            onClick={open}
          />
        </BonusAdminActionGate>
      </Row>
      <HeaderDiv>
        <IconDiv />
        <Typography.Body type={'Label'}>Name</Typography.Body>
      </HeaderDiv>
      <ListDiv>
        {users.map((user) => (
          <UserRow key={user.id} user={user} app={app} />
        ))}
      </ListDiv>
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <AddAdminsPopup
              close={close}
              availableUsers={adminsAndEditorsWhoAreNotAddedYet}
              app={app}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default AppAdminsList;
